import { useState, useRef, useEffect, memo } from "react";

/**
 * LazyImage component that only loads images when they're about to enter the viewport.
 * Uses Intersection Observer for efficient lazy loading.
 */
const LazyImage = memo(({ 
  src, 
  alt, 
  onLoad, 
  onError,
  style = {},
  className = "",
  rootMargin = "200px", // Start loading 200px before entering viewport
  threshold = 0.01
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Stop observing once in view
            if (observerRef.current) {
              observerRef.current.unobserve(element);
            }
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [rootMargin, threshold]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = (e) => {
    setHasError(true);
    console.error("❌ LazyImage failed to load:", src);
    onError?.(e);
  };

  return (
    <div 
      ref={imgRef}
      className={`lazy-image-container ${className}`}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        ...style
      }}
    >
      {/* Placeholder/skeleton shown while loading */}
      {!isLoaded && !hasError && (
        <div 
          className="lazy-image-placeholder"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: "8px",
          }}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div 
          className="lazy-image-error"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            borderRadius: "8px",
            color: "#999",
            fontSize: "12px",
          }}
        >
          Failed to load
        </div>
      )}

      {/* Actual image - only load src when in view and src is valid */}
      {isInView && !hasError && src && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            ...style
          }}
          // Native lazy loading as fallback
          loading="lazy"
          // Disable dragging for better UX
          draggable={false}
        />
      )}

      {/* Show error if no src provided */}
      {isInView && !src && !hasError && (
        <div 
          className="lazy-image-error"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            borderRadius: "8px",
            color: "#999",
            fontSize: "12px",
          }}
        >
          No image URL
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = "LazyImage";

export default LazyImage;

